import React from 'react';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import Loader from 'components/partials/loader/loader';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';

import Icons from 'assets/icons';

import type { QuickSummaryData } from './use-quick-summary';

interface SummaryRowProps {
  label: string;
  value: string;
}

const SummaryRow = ({ label, value }: SummaryRowProps): React.JSX.Element => (
  <div className="flex items-start gap-1.5 text-sm leading-5 text-gray-700">
    <div className="flex-1 min-w-0">
      <TruncatedCell value={label} />
    </div>
    <span className="shrink-0 text-right">{value}</span>
  </div>
);

interface QuickSummaryPanelProps {
  data: QuickSummaryData | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  panelRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onFullResults: () => void;
}

const QuickSummaryPanel = ({
  data,
  isLoading,
  isError,
  errorMessage,
  panelRef,
  onClose,
  onFullResults,
}: QuickSummaryPanelProps): React.JSX.Element => (
  <div
    ref={panelRef}
    className="absolute right-0 top-full z-10 w-[300px] rounded-md border border-navy-100 bg-white p-5 shadow-md"
  >
    <div className="flex items-start justify-between">
      <h3 className="text-base font-semibold leading-6 text-navy-800">
        Awarding summary
      </h3>
      <IconButton
        size="xs"
        variant="reserve-text"
        icon={<Icons.Close />}
        onClick={onClose}
        className="-mr-2 -mt-2"
        aria-label="Close summary"
        data-testid="quick-summary-close"
      />
    </div>

    <div className="relative max-h-[700px] overflow-y-auto scrollbar-thin">
      {isLoading && (
        <div className="flex min-h-[100px] items-center justify-center">
          <Loader
            loading
            overlay="white"
            customClass="bg-white"
            fullScreen={false}
          />
        </div>
      )}

      {!isLoading && isError && (
        <p className="pt-5 text-center text-sm text-critical-600">
          {errorMessage}
        </p>
      )}

      <div
        className="grid transition-[grid-template-rows] duration-300"
        style={{
          gridTemplateRows: !isLoading && !isError && data ? '1fr' : '0fr',
        }}
        aria-live="polite"
        role="status"
      >
        <div className="overflow-hidden">
          {data && (
            <div className="flex flex-col gap-4 pt-4">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold leading-5 text-gray-700">
                  Future Year Projections
                </p>
                <SummaryRow
                  label="Estimated Enrollment"
                  value={data.estimatedEnrollment}
                />
                <SummaryRow label="Estimated NTR" value={data.estimatedNtr} />
                <SummaryRow label="Average NTR" value={data.estimatedAvgNtr} />
                <SummaryRow
                  label="Estimated Discount Rate"
                  value={data.estimatedDiscountRate}
                />
                {data.enrollmentChange && (
                  <SummaryRow
                    label="Change in enrollment"
                    value={data.enrollmentChange}
                  />
                )}
              </div>

              {data.hasActualData && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold leading-5 text-gray-700">
                    Current Year Outcomes
                  </p>
                  <SummaryRow
                    label="Number Enrolled"
                    value={data.actualEnrollment}
                  />
                  <SummaryRow label="NTR" value={data.actualNtr} />
                  <SummaryRow label="Average NTR" value={data.actualAvgNtr} />
                  <SummaryRow
                    label="Tuition Discount"
                    value={data.actualDiscountRate}
                  />
                </div>
              )}

              {data.hasTuitionData && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold leading-5 text-gray-700">
                    Tuition &amp; Fees
                  </p>
                  <p className="text-sm">{data.tuitionCurrentYearLabel}</p>
                  {data.tuitionCurrentYearGroups.map((group) => (
                    <SummaryRow
                      key={group.name}
                      label={group.name}
                      value={group.value}
                    />
                  ))}
                  <p className="text-sm">{data.tuitionFutureYearLabel}</p>
                  {data.tuitionFutureYearGroups.map((group) => (
                    <SummaryRow
                      key={group.name}
                      label={group.name}
                      value={group.value}
                    />
                  ))}
                </div>
              )}

              <Button
                type="button"
                variant="brand-outlined"
                size="sm"
                onClick={onFullResults}
                className="w-fit shadow-sm"
                data-testid="quick-summary-full-results"
              >
                Full results
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default QuickSummaryPanel;
